import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, colors } from '@innovationdepartment/proxima-ui';
import { Link } from 'react-router';

/* Name | Status | Destinations | Created | Last run | Actions
   Data tracks are fixed and Name takes the slack, as the workflows list does.
   Below the min width the list scrolls sideways rather than squeezing columns together. */
const ROW_MIN_WIDTH = 1040;

export const audienceRowGrid = css`
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 128px 220px 190px 110px 48px;
  gap: 16px;
  align-items: center;
  min-width: ${ROW_MIN_WIDTH}px;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding-bottom: 16px;
`;

export const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-x: auto;
`;

export const HeaderLabels = styled.div`
  ${audienceRowGrid};
  padding: 0 24px;

  span {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${colors.neutral60};
  }
`;

export const Card = styled.div<{ failed: boolean }>`
  background: ${colors.white};
  border: 1px solid ${colors.neutral20};
  border-left: ${({ failed }) =>
    failed ? `3px solid ${colors.red60}` : `1px solid ${colors.neutral20}`};
  border-radius: 8px;
  overflow: hidden;
  min-width: fit-content;
`;

export const RowMain = styled.div`
  ${audienceRowGrid};
  padding: 18px 24px;
  cursor: pointer;

  &:hover {
    background: ${colors.neutral0};
  }
`;

export const NameCell = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/* The row's link to its detail page; looks like the name, focuses like a link */
export const NameLink = styled(Link)`
  display: block;
  min-width: 0;
  color: inherit;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

export const ErrorLine = styled.div`
  font-size: 13px;
  color: ${colors.red60};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

/* A failed delivery's error under its status pill, narrow enough to keep the table's other columns in view */
export const DeliveryErrorLine = styled(ErrorLine)`
  max-width: 240px;
  margin-top: 4px;
  white-space: normal;
`;

/* A lookup for this row failed; the row still renders what did load */
export const LoadWarning = styled.div`
  font-size: 13px;
  color: ${colors.orange70};
`;

export const Stacked = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/* Created and Last run: date, time, then the creator, all top-aligned so the two cells' lines match */
export const TimestampCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-self: start;
  min-width: 0;
`;

/* One line that clips with an ellipsis; the full value is in its title */
export const ClippedLine = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ChipColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const DOT_COLORS: Record<string, string> = {
  active: colors.green60,
  populating: colors.yellow60,
  pending: colors.neutral40,
  deleted: colors.neutral30
};

export const DestinationChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border: 1px solid ${colors.neutral20};
  border-radius: 999px;
  font-size: 12px;
  color: ${colors.neutral90};
  white-space: nowrap;

  small {
    font-size: 12px;
    color: ${colors.neutral60};
  }
`;

export const StatusDot = styled.span<{ status: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ status }) => DOT_COLORS[status] ?? colors.neutral40};
`;

export const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const iconActionBox = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${colors.neutral30};
  border-radius: 4px;
  background: transparent;
  color: ${colors.neutral80};
  cursor: pointer;
  text-decoration: none;

  svg {
    font-size: 18px;
  }

  &:hover:not(:disabled) {
    background: ${colors.neutral5};
    color: ${colors.neutral100};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const IconButton = styled.button`
  ${iconActionBox};
`;

export const IconLink = styled(Link)`
  ${iconActionBox};
`;

export const ExpandedPanel = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 1fr) minmax(480px, 1.4fr);
  gap: 20px;
  padding: 20px 24px 24px;
  background: ${colors.neutral0};
  border-top: 1px solid ${colors.neutral20};
`;

export const Panel = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.neutral20};
  border-radius: 8px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

export const PanelLinks = styled.div`
  grid-column: 1 / -1;
  display: flex;
  gap: 24px;
  font-size: 14px;

  a {
    color: ${colors.blue60};
    text-decoration: none;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${colors.neutral60};
    padding: 6px 8px;
    border-bottom: 1px solid ${colors.neutral20};
    white-space: nowrap;
  }

  td {
    padding: 10px 8px;
    border-bottom: 1px solid ${colors.neutral10};
    color: ${colors.neutral90};
    vertical-align: middle;
  }

  tr:last-of-type td {
    border-bottom: none;
  }

  /* Secondary text, such as an ad account's id, keeps the cell's size and reads lighter */
  small {
    color: ${colors.neutral60};
    font-size: inherit;
  }

  a {
    color: ${colors.blue60};
    text-decoration: none;
  }
`;

/* A nested row's first cell content, indented behind its tree glyph */
export const NestedRowLead = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding-left: 12px;
`;

export const TreeGlyph = styled.span`
  color: ${colors.neutral40};
`;

export const TableScroll = styled.div`
  overflow-x: auto;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px 24px;
  border: 1px dashed ${colors.neutral30};
  border-radius: 8px;
  background: ${colors.neutral0};
  text-align: center;
`;

export const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
`;

/* A failed list load's message with its Retry button */
export const LoadErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ConfirmContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 320px;
`;

export const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const BackLink = styled(Link)`
  display: inline-flex;
  color: ${colors.neutral70};
  text-decoration: none;

  /* Beats the svg fill a ButtonWithPopup wrapper sets, so the arrow keeps the link color */
  && svg {
    fill: currentColor;
  }

  &:hover {
    color: ${colors.neutral100};
  }

  &[aria-disabled='true'] {
    color: ${colors.neutral40};
    cursor: default;
  }
`;

/* Text for screen readers only, e.g. why a control is disabled */
export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

/* Contained proxima-ui Button whose disabled state reads as a solid gray button.
   proxima-ui paints disabled contained buttons neutral5 with neutral30 text, which vanishes on a neutral5 header.
   The doubled class outranks the theme's `.MuiButton-contained.Mui-disabled` rule, and `> *` reaches the label and icon spans.
   MUI sets `pointer-events: none` on disabled buttons, so a not-allowed cursor never shows. */
export const PrimaryButton = styled(Button)`
  &&.Mui-disabled {
    background-color: ${colors.neutral20};
    color: ${colors.neutral50};
    opacity: 1;
    box-shadow: none;
  }

  &&.Mui-disabled > * {
    color: ${colors.neutral50};
  }
`;
